import { useEffect, useRef, useState } from "react";
import { useNavigate } from "@remix-run/react";
import { SearchResultItem } from "~/.server/content.server";

interface SearchInputProps {
  placeholder?: string;
  className?: string;
  expanded?: boolean;
  focusOnLoad?: boolean;
}

export function SearchInput({ 
  placeholder = "Search for help...", 
  className = "", 
  expanded = false,
  focusOnLoad = false
}: SearchInputProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResultItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  
  // Focus the input on load if requested
  useEffect(() => {
    if (focusOnLoad && inputRef.current) {
      inputRef.current.focus();
    }
  }, [focusOnLoad]);
  
  // Handle clicks outside the results dropdown to close it
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        resultsRef.current && 
        !resultsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  // Handle search query changes
  useEffect(() => {
    const searchTimer = setTimeout(async () => {
      if (query.trim() === "") {
        setResults([]);
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      try {
        const response = await fetch(`/api/search?q=${encodeURIComponent(query)}&limit=5`);
        if (response.ok) {
          const data = await response.json();
          setResults(data.results);
          setShowResults(true);
        }
      } catch (error) {
        console.error("Error fetching search results:", error);
      } finally {
        setIsLoading(false);
      }
    }, 300); // debounce for 300ms
    
    return () => clearTimeout(searchTimer);
  }, [query]);
  
  // Handle form submission - navigate to search results page
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query)}`);
      setShowResults(false);
    }
  };
  
  // Handle clicking or selecting an autocomplete result
  const handleResultSelect = (slug: string, type: 'article' | 'video') => {
    navigate(`/${type === 'video' ? 'videos' : 'kb'}/${slug}`);
    setShowResults(false);
    setQuery("");
  };
  
  // Handle keyboard navigation in the results
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // If no results are shown, don't handle keyboard navigation
    if (!showResults || results.length === 0) return;
    
    // Down arrow
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => (prev < results.length - 1 ? prev + 1 : prev));
    }
    // Up arrow
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => (prev > 0 ? prev - 1 : 0));
    }
    // Enter to select the highlighted result
    else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      const selectedResult = results[activeIndex];
      handleResultSelect(selectedResult.slug, selectedResult.type);
    }
    // Escape to close the results dropdown
    else if (e.key === 'Escape') {
      setShowResults(false);
      setActiveIndex(-1);
      inputRef.current?.blur();
    }
  };
  
  return (
    <div className={`relative ${className}`}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => query.trim() !== "" && setShowResults(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className={`
              w-full rounded-lg border border-slate-300 px-4 py-2 pr-10
              focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200
              ${expanded ? 'bg-white' : 'bg-slate-100'} 
              ${expanded ? 'text-base' : 'text-sm'}
              text-slate-800
            `}
            aria-label="Search"
            aria-autocomplete="list"
            aria-controls={showResults ? "search-results" : undefined}
            aria-activedescendant={activeIndex >= 0 ? `search-result-${results[activeIndex].id}` : undefined}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {isLoading ? (
              <svg className="h-5 w-5 animate-spin text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="h-5 w-5 text-slate-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </div>
      </form>
      
      {/* Autocomplete results dropdown */}
      {showResults && results.length > 0 && (
        <div
          ref={resultsRef}
          id="search-results"
          role="listbox"
          aria-label="Search results"
          className="absolute z-10 mt-2 w-full overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
        >
          <div className="max-h-96 overflow-y-auto">
            {results.map((result, index) => (
              <div 
                key={result.id}
                id={`search-result-${result.id}`}
                role="option"
                aria-selected={activeIndex === index}
                tabIndex={0}
                onClick={() => handleResultSelect(result.slug, result.type)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleResultSelect(result.slug, result.type);
                  }
                }}
                className={`cursor-pointer border-b border-slate-100 p-3 hover:bg-slate-50 ${
                  activeIndex === index ? 'bg-slate-100' : ''
                }`}
              >
                <div className="mb-1 font-medium text-slate-800" 
                     dangerouslySetInnerHTML={{ __html: result.highlightedTitle || result.title }}>
                </div>
                <div className="text-sm text-slate-600 line-clamp-2" 
                     dangerouslySetInnerHTML={{ __html: result.highlightedSummary || result.summary }}>
                </div>
                <div className="mt-1 flex items-center text-xs">
                  <span className={`mr-2 rounded-full px-2 py-0.5 ${
                    result.type === 'video' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {result.type === 'video' ? 'Video' : 'Article'}
                  </span>
                  <span className="text-slate-500">{result.category}</span>
                </div>
              </div>
            ))}
            <div className="border-t border-slate-100 bg-slate-50 p-2 text-center">
              <button 
                onClick={handleSubmit}
                className="text-sm font-medium text-blue-600 hover:text-blue-800"
              >
                See all results
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 